// API Configuration
// 중앙화된 API 설정 관리

import { logger } from '../utils/logger';

interface ApiConfig {
  apiUrl: string;
  wsUrl: string;
  timeout: number;
  orgId: string;
}

// 환경별 기본 설정
const getDefaultConfig = (): ApiConfig => {
  const environment = process.env.NODE_ENV;

  // 기본 포트 설정 (환경별) - 백엔드 HTTP 포트에 맞게 수정
  // 수정됨 2025-09-16: 백엔드가 HTTP 포트 8000에서 실행 중
  const defaultApiPort = '8000'; // 메인 백엔드는 HTTP 8000
  const defaultWsPort = '8000'; // WebSocket은 항상 메인 백엔드와 같은 포트

  // 현재 브라우저의 호스트를 사용하여 동적으로 설정
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  
  // 환경 변수가 설정되어 있으면 우선 사용
  if (process.env.REACT_APP_API_URL) {
  }
  
  // HTTPS 환경 감지 (프로덕션 또는 HTTPS 개발 환경)
  const isHttps = typeof window !== 'undefined' 
    ? window.location.protocol === 'https:' 
    : process.env.NODE_ENV === 'production';
  
  // nginx를 통해 접속한 경우 (도메인 또는 내부망 IP)
  const isNginxAccess = typeof window !== 'undefined' && 
    (currentHost === 'newattend.com' || 
     currentHost === 'www.newattend.com' ||
     currentHost === 'studyroom.newattend.com' ||
     currentHost === '192.168.0.38');  // 내부망도 nginx 처리
  
  // 내부망 IP로 직접 접속한 경우 (포트 3443)
  const isDirectAccess = typeof window !== 'undefined' && 
    window.location.port === '3443';
  
  // 내부망 접속 상세 디버깅
  const isInternalNetwork = typeof window !== 'undefined' && 
    currentHost === '192.168.0.38' && window.location.port === '3443';
  
  console.log('🔍 내부망 접속 상세 감지:', {
    currentHost,
    port: typeof window !== 'undefined' ? window.location.port : 'N/A',
    protocol: typeof window !== 'undefined' ? window.location.protocol : 'N/A',
    href: typeof window !== 'undefined' ? window.location.href : 'N/A',
    isDirectAccess,
    isInternalNetwork,
    isNginxAccess
  });
  
  // API URL 설정 - 호스트 자동 감지
  let apiUrl = process.env.REACT_APP_API_URL;

  // 환경 변수가 없으면 호스트 기반 자동 설정
  if (!apiUrl) {
    // 1. 실제 운영 서비스 (newattend.com) 🔴
    if (environment === 'production' ||
        currentHost === 'newattend.com' ||
        currentHost === 'www.newattend.com' ||
        currentHost === 'studyroom.newattend.com' || currentHost.startsWith('studyroom.')) {
      // nginx를 통한 API 프록시 (상대 경로)
      apiUrl = '';
    }
    // 2. 테스트 환경 (faceattend.store) 🟡
    else if (currentHost === 'faceattend.store' ||
             currentHost === 'studyroom.faceattend.store' ||
             currentHost === 'studyroom.staging.local' ||
             currentHost.includes('.staging.local')) {
      // nginx를 통한 API 프록시 (상대 경로)
      apiUrl = '';
    }
    // 3. N100 스테이징 서버 (내부망: 192.168.0.38, 외부망: 180.70.238.90) 🔧
    else if (currentHost === '192.168.0.38' || currentHost.startsWith('192.168.')) {
      // nginx 프록시 사용 (상대 경로) - Mixed Content 방지
      apiUrl = '';
    }
    else if (currentHost === '180.70.238.90') {
      // 외부망 접속: nginx 프록시 사용 (상대 경로)
      apiUrl = '';
    }
    // 4. localhost (개발자 PC 또는 로컬 서버)
    else if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      // 현재 포트에 맞게 동적 설정 (로컬 윈도우: 8080, 개발: 8000)
      const currentPort = typeof window !== 'undefined' ? window.location.port : '8000';
      const apiPort = currentPort === '8080' ? '8080' : '8000';
      apiUrl = `http://localhost:${apiPort}`;
    }
    // 5. 기타 환경 (폴백) - nginx 프록시 사용
    else {
      apiUrl = '';
    }
  }
  
  // WebSocket URL 설정 - 런타임 동적 설정 (빌드 최적화 방지)
  let wsUrl = process.env.REACT_APP_WS_URL;
  if (!wsUrl) {
    // 런타임에 동적으로 WebSocket URL 결정
    const getWebSocketUrl = () => {
      const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws';

      // 운영 서비스
      if (host === 'newattend.com' || host === 'www.newattend.com' ||
          host === 'studyroom.newattend.com' || host.startsWith('studyroom.')) {
        return `${protocol}://${host}/ws`;
      }
      // 테스트 환경
      if (host === 'faceattend.store' || host === 'studyroom.faceattend.store' ||
          host === 'studyroom.staging.local' || host.includes('.staging.local')) {
        return `${protocol}://${host}/ws`;
      }
      // N100 스테이징 (nginx 프록시)
      if (host === '192.168.0.38') {
        return `${protocol}://${host}:8000`;
      }
      // 로컬 윈도우/기타 내부망 (현재 포트 사용)
      if (host.startsWith('192.168.')) {
        const currentPort = typeof window !== 'undefined' ? window.location.port : '8080';
        return `${protocol}://${host}:${currentPort}`;
      }
      // N100 외부망
      if (host === '180.70.238.90') {
        return `${protocol}://${host}/ws`;
      }
      // localhost (현재 포트 사용)
      if (host === 'localhost' || host === '127.0.0.1') {
        const currentPort = typeof window !== 'undefined' ? window.location.port : '8000';
        return `ws://${host}:${currentPort}`;
      }
      // 폴백 - nginx 프록시 사용
      return `${protocol}://${host}/ws`;
    };

    wsUrl = getWebSocketUrl();
  }

  console.log('🔍 DEBUG - API Configuration:', {
    currentHost,
    isNginxAccess,
    isDirectAccess,
    apiUrl,
    wsUrl,
    isHttps,
    windowProtocol: typeof window !== 'undefined' ? window.location.protocol : 'N/A',
    windowPort: typeof window !== 'undefined' ? window.location.port : 'N/A',
  });

  // nginx를 통한 접속일 때 빈 문자열 유지 (nginx가 처리)
  const finalApiUrl = apiUrl;
  
  return {
    apiUrl: finalApiUrl,
    wsUrl,
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000'),
    orgId: process.env.REACT_APP_ORG_ID || 'dev-org-1',
  };
};

// API 설정 내보내기
export const apiConfig = getDefaultConfig();

// 개별 설정 내보내기 (기존 코드 호환성)
export const API_BASE_URL = apiConfig.apiUrl;
export const WS_BASE_URL = apiConfig.wsUrl;
export const API_TIMEOUT = apiConfig.timeout;
export const DEFAULT_ORG_ID = apiConfig.orgId;

// 환경 정보 로깅 (개발 환경에서만)
logger.info('🔍 API Configuration:', {
  environment: process.env.NODE_ENV,
  apiUrl: apiConfig.apiUrl,
  wsUrl: apiConfig.wsUrl,
  timeout: apiConfig.timeout,
  orgId: apiConfig.orgId,
});
